import React, { useEffect, useState, useCallback } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, DollarSign, ClipboardCheck, Award } from 'lucide-react';
import api from '../../config/api';

const RANGE_OPTIONS = [
  { label: '6 months', value: 6 },
  { label: '12 months', value: 12 },
  { label: '24 months', value: 24 },
];

function TrendCard({ icon: Icon, color, title, dataKey, data, formatter, suffix }: any) {
  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} color={color} />
        </div>
        <h3 style={{ fontWeight: 600, fontSize: 15 }}>{title}</h3>
      </div>
      {data?.length > 0 ? (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={v => suffix ? `${v}${suffix}` : v} />
            <Tooltip formatter={(v: any) => [formatter ? formatter(v) : v, title]} />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      ) : <div className="empty-state"><p>No data for this period</p></div>}
    </div>
  );
}

export default function Analytics() {
  const [data, setData]     = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/reports/analytics', { params: { months } });
      setData(r.data.data);
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => { doFetch(); }, [doFetch]);

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="page-subtitle">Trends across enrollment, revenue, attendance and academic performance</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`btn ${months === opt.value ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              onClick={() => setMonths(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 80 }}>
          <div className="loader loader-dark" style={{ margin: '0 auto' }} />
        </div>
      ) : data && (
        <>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <TrendCard
              icon={Users} color="#1a56db" title="Enrollment Trend" dataKey="count"
              data={data.enrollmentTrend} formatter={(v: any) => `${v} new students`}
            />
            <TrendCard
              icon={DollarSign} color="#0e9f6e" title="Revenue Trend" dataKey="amount"
              data={data.revenueTrend} formatter={(v: any) => `KES ${Number(v).toLocaleString()}`}
            />
          </div>

          <div className="grid-2" style={{ marginBottom: 20 }}>
            <TrendCard
              icon={ClipboardCheck} color="#d97706" title="Attendance Rate Trend" dataKey="rate"
              data={data.attendanceTrend} formatter={(v: any) => `${v}%`} suffix="%"
            />
            <TrendCard
              icon={Award} color="#7c3aed" title="Exam Pass-Rate Trend" dataKey="rate"
              data={data.passRateTrend} formatter={(v: any) => `${v}%`} suffix="%"
            />
          </div>

          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp size={18} color="var(--primary)" />
              </div>
              <h3 style={{ fontWeight: 600, fontSize: 15 }}>Department Performance</h3>
            </div>
            {data.departmentPerformance?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.departmentPerformance}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="department" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="pass_rate" name="Pass Rate %" fill="#1a56db" radius={[6,6,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="table-container" style={{ marginTop: 16 }}>
                  <table>
                    <thead>
                      <tr><th>Department</th><th>Students</th><th>Avg Score</th><th>Pass Rate</th></tr>
                    </thead>
                    <tbody>
                      {data.departmentPerformance.map((d: any) => (
                        <tr key={d.department}>
                          <td style={{ fontWeight: 500 }}>{d.department}</td>
                          <td style={{ textAlign: 'center' }}>{d.students}</td>
                          <td style={{ textAlign: 'center' }}>{d.avg_score ?? '—'}</td>
                          <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--success)' }}>{d.pass_rate != null ? `${d.pass_rate}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : <div className="empty-state"><p>No department data</p></div>}
          </div>
        </>
      )}
    </div>
  );
}
